import { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { useTrackScreen } from '@/lib/analytics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { InterviewOnboardingCard } from '@/components/interview/InterviewOnboardingCard';

import {
  CheckCircle,
  ArrowRight,
  Sparkles,
  User,
  BarChart3,
  Compass,
  Linkedin,
  Briefcase,
  Building2,
  Crown,
  Star,
  AlertCircle,
} from 'lucide-react-native';
import { reportError } from '@/lib/errors';
import Animated, { FadeInUp, ZoomIn } from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import {
  OnboardingLayout,
  STARTUP_ONBOARDING_STEPS,
  INVESTOR_ONBOARDING_STEPS,
} from '@/components/OnboardingLayout';
import {
  useAuth,
  isSupabaseConfigured,
  supabase,
} from '@/lib/supabase';

import { mode } from '@/lib/config/app-mode';
import { useScoreStore } from '@/lib/state/score-store';
import { computeOnboardingComplete } from '@/lib/onboarding/completion';
import { buildProfileOnboardingUpdate } from '@/lib/supabase/profile-mappers';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { useLatestScoreResult } from '@/lib/supabase/score-queries';
import { useEntrance } from '@/lib/a11y/reduced-motion';
import { useAiConsentGate } from '@/lib/consent/ai-consent';

export default function OnboardingCompleteScreen() {
  useTrackScreen('OnboardingComplete');
  const onboardingTracked = useRef(false);
  const saveAttempted = useRef(false);
  useEffect(() => {
    if (onboardingTracked.current) return;
    onboardingTracked.current = true;
    track('onboarding_completed');
  }, []);
  const router = useRouter();
  const colors = useSystemThemeColors();
  const entrance = useEntrance();
  const firstScoreTracked = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const userRole = useProfileStore((s) => s.userRole);

  // Auth state
  const { user, profile: authProfile, refreshProfile } = useAuth();

  // Store state (read profile data collected during onboarding)
  const localStartupProfile = useProfileStore((s) => s.startupProfile);
  const localInvestorProfile = useProfileStore((s) => s.investorProfile);
  const localUser = useProfileStore((s) => s.user);

  // Store actions
  const completeOnboarding = useProfileStore((s) => s.completeOnboarding);
  const triggerAIScoring = useScoreStore((s) => s.triggerAIScoring);

  const isStartup = userRole === 'startup';

  // Determine if user provided data during onboarding
  const hasUploadedPitchDeck = !!(localStartupProfile?.profileSource === 'deck');
  const hasConnectedDataRoom = !!(localStartupProfile?.dataRoomConnected);
  const hasProvidedData = hasUploadedPitchDeck || hasConnectedDataRoom;

  // Predicate-driven completion (SDD Task 11) — a founder is only "complete"
  // once they have an active team membership, a finished personal profile,
  // and SOME data source (deck, data room, or a real score_event). This is
  // the guard that stops empty profiles being marked complete. Investors have
  // no team_members / data-source failure mode, so they stay unconditionally
  // complete once they reach this screen.
  const {
    startupId: membershipStartupId,
    isInvitee: resolvedIsInvitee,
    isLoading: myStartupLoading,
  } = useMyStartup();
  const hasActiveMembership = !!membershipStartupId;
  // Provenance-based (team_members.invited_by), NOT !isOwner — see
  // use-my-startup.ts module doc. Defaulting to false while loading is the
  // correct strict direction (never skip the data-source gate speculatively).
  const isInvitee = !myStartupLoading && resolvedIsInvitee;
  const personalProfileDone = !!authProfile?.first_name;

  const scoreStartupId = membershipStartupId ?? localStartupProfile?.id ?? null;
  const { data: latestScoreEvent, isLoading: scoreEventLoading } = useLatestScoreResult(scoreStartupId ?? '');
  const hasScoreEvent = !!latestScoreEvent;

  // Activation §8 — founder "activated" = a REAL first score_event landed.
  // This is the reachable, honest signal (not a screen view): fire once when
  // the score query actually resolves to a persisted score for a founder.
  useEffect(() => {
    if (firstScoreTracked.current) return;
    if (isStartup && hasScoreEvent) {
      firstScoreTracked.current = true;
      track('first_score');
    }
  }, [isStartup, hasScoreEvent]);

  // Only wait on the remote score_event round-trip when local data-source
  // signals are both false — a deck/data room already present means the
  // predicate passes without needing that query to resolve first.
  const dataSignalsLoading = isStartup && (myStartupLoading || (!hasProvidedData && scoreEventLoading));

  const onboardingComplete = isStartup
    ? computeOnboardingComplete({
        hasActiveMembership,
        isInvitee,
        personalProfileDone,
        dataSource: {
          hasPitchDeck: hasUploadedPitchDeck,
          hasDataRoom: hasConnectedDataRoom,
          hasScoreEvent,
        },
      })
    : true;

  // Post-onboarding interview trigger — gate on REAL signals: a founder who
  // finished WITHOUT a data room AND has not already completed an interview is
  // the ideal no-data-room candidate (~80% of founders). Data-room founders and
  // those who already did an interview are never prompted.
  const startupId = localStartupProfile?.id;
  const { data: hasCompletedInterview } = useQuery({
    queryKey: ['interview', 'completed', startupId ?? ''],
    queryFn: async (): Promise<boolean> => {
      if (!startupId || mode.isMock || !isSupabaseConfigured()) return false;
      const { data, error } = await (supabase.from('interview_sessions') as any)
        .select('id')
        .eq('startup_id', startupId)
        .eq('status', 'completed')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: isStartup && !hasConnectedDataRoom && !!startupId,
  });
  const [interviewPromptDismissed, setInterviewPromptDismissed] = useState(false);
  const showInterviewPrompt =
    isStartup && !hasConnectedDataRoom && !hasCompletedInterview && !interviewPromptDismissed;

  const handleStartInterview = () => {
    track('onboarding_data_choice', { choice: 'interview', source: 'onboarding-complete' });
    router.push('/(startup)/interview');
  };

  // Mark onboarding complete in local store immediately on mount
  useEffect(() => {
    if (saveAttempted.current) return;
    saveAttempted.current = true;
    completeOnboarding?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to dashboard after flipping onboarding_completed.
  // Now that updateUser() lock contention is eliminated, this await is fast.
  // Only wired to the CTA once `onboardingComplete` is true (see render below);
  // the `onboardingComplete` guard below is defensive against a stale closure.
  //
  // AI-scoring consent gate. `runWithConsent` shows the sheet the first time
  // a founder reaches this screen with data to score, and calls its action
  // right after acceptance. Declining must NOT trap the founder here —
  // `onDecline` runs the same navigation tail as acceptance, just without
  // ever having called `triggerAIScoring`.
  const proceedToDashboard = useCallback(() => {
    // Activation §8 — an invited member reaching the workspace is their
    // activation endpoint. Fired after the awaited onboarding-complete write,
    // only for the invite path (founders/investors have their own funnels).
    if (isInvitee) track('entered_workspace');
    // Navigate to / — landing page reads refreshed AuthProvider cache
    router.replace('/');
  }, [isInvitee, router]);
  const scoringConsentGate = useAiConsentGate(['ai_scoring'], {
    onDecline: proceedToDashboard,
  });
  // `handleGoToDashboard` awaits a profile update + `refreshProfile()` before
  // it ever reaches the gate — either of which can re-render this component
  // (a refreshed AuthProvider cache is exactly the kind of thing that does).
  // A ref read AFTER the awaits, rather than the `scoringConsentGate` value
  // closed over when the press started, is what makes the gate decision use
  // consent/loading state as of NOW rather than as of the button press.
  const scoringConsentGateRef = useRef(scoringConsentGate);
  scoringConsentGateRef.current = scoringConsentGate;

  const handleGoToDashboard = async () => {
    setIsSaving(true);
    completeOnboarding?.();

    // Flip onboarding_completed (nulls onboarding_step too) + refresh
    // AuthProvider cache — gated on the predicate, never unconditional.
    if (isSupabaseConfigured() && user && onboardingComplete) {
      try {
        const { error } = await (supabase.from('profiles') as any)
          .update(buildProfileOnboardingUpdate('complete', true))
          .eq('id', user.id);

        if (error) {
          reportError(error, { context: 'OnboardingComplete.markComplete', silent: true });
        }

        await refreshProfile();
      } catch (err) {
        reportError(err, { context: 'OnboardingComplete.markComplete', silent: true });
      }
    }

    if (isStartup && hasProvidedData && !mode.showSampleData && localStartupProfile) {
      // Fire-and-forget AI scoring, gated on explicit consent. The sheet (if
      // shown) blocks navigation only long enough for the founder to decide —
      // declining still reaches the dashboard, via `onDecline` above.
      scoringConsentGateRef.current.runWithConsent(() => {
        triggerAIScoring({ type: 'onboarding' }, localStartupProfile).catch((err) => {
          reportError(err, { context: 'OnboardingComplete.triggerAIScoring', level: 'warning', silent: true });
        });
        proceedToDashboard();
      });
    } else {
      proceedToDashboard();
    }
  };

  // Not-complete branch (founder with no data source yet): nudge back to the
  // data-source step instead of flipping onboarding_completed. No flag write —
  // the DB predicate stays false until they actually add a deck/data
  // room/interview, so a re-entry into onboarding resumes here correctly.
  const handleAddData = () => {
    track('onboarding_data_choice', { choice: 'nudge', source: 'onboarding-complete' });
    router.push('/(onboarding)/data-input-choice');
  };

  // Startup "What's next" cards
  const startupNextCards = [
    {
      icon: <User size={20} color={colors.primary[600]} />,
      title: 'Complete your profile',
      description: 'Add problem, traction, market details',
    },
    {
      icon: <BarChart3 size={20} color={colors.primary[600]} />,
      title: 'Get your readiness score',
      description: hasProvidedData ? 'Your score is being calculated' : 'Provide data to receive your score',
    },
    {
      icon: <Compass size={20} color={colors.primary[600]} />,
      title: 'Explore the platform',
      description: 'Dashboard, data room, investor matching',
    },
  ];

  // Calculate investor portfolio stats
  const portfolioCount = localInvestorProfile?.investmentHistory?.length ?? 0;
  const boardSeats = localInvestorProfile?.investmentHistory?.filter(
    (h) => h.investorRole === 'board-member'
  ).length ?? 0;
  const advisoryRoles = localInvestorProfile?.investmentHistory?.filter(
    (h) => h.investorRole === 'advisor' || h.investorRole === 'board-observer'
  ).length ?? 0;

  const steps = isStartup ? STARTUP_ONBOARDING_STEPS : INVESTOR_ONBOARDING_STEPS;
  const entityName = isStartup ? localStartupProfile?.name : localUser?.name;

  return (
    <OnboardingLayout
      currentStep="complete"
      steps={steps}
      title="You're All Set!"
      subtitle={isStartup
        ? 'Your startup profile is ready. Let\'s get you investment ready.'
        : 'Your investor profile is ready. Discover quality dealflow.'}
      userType={isStartup ? 'startup' : 'investor'}
      entityName={entityName}
      showBack={false}
    >
      <View className="flex-1 px-1">
        {/* Success Animation */}
        <View className="items-center py-6">
          <Animated.View
            entering={entrance(ZoomIn.delay(300).duration(600).springify())}
            className="items-center mb-8"
          >
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-6"
              style={{ backgroundColor: colors.traffic.greenLight }}
            >
              <CheckCircle size={48} color={colors.traffic.green} />
            </View>

            <Text
              style={{ color: colors.text.primary }}
              className="text-2xl font-bold text-center mb-2"
            >
              You're All Set!
            </Text>

            {/* Brief summary of what was set up */}
            {isStartup && localStartupProfile && (
              <View className="items-center mt-1">
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-base text-center"
                >
                  {localStartupProfile.name}
                  {localStartupProfile.industry ? ` \u00b7 ${localStartupProfile.industry}` : ''}
                </Text>
              </View>
            )}

          </Animated.View>

          {/* What's Next Cards (Startup) */}
          {isStartup && (
            <Animated.View
              entering={entrance(FadeInUp.delay(600).duration(600))}
              className="w-full"
            >
              <View
                style={{
                  backgroundColor: colors.surface.primary,
                  borderRadius: 20,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <View className="flex-row items-center gap-2 mb-4">
                  <Sparkles size={18} color={colors.primary[600]} />
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold"
                  >
                    What's Next
                  </Text>
                </View>

                <View className="gap-3">
                  {startupNextCards.map((card) => (
                    <View key={`next-card-${card.title}`} className="flex-row items-center gap-3">
                      <View
                        className="w-10 h-10 rounded-xl items-center justify-center"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        {card.icon}
                      </View>
                      <View className="flex-1">
                        <Text
                          style={{ color: colors.text.primary }}
                          className="text-sm font-semibold"
                        >
                          {card.title}
                        </Text>
                        <Text
                          style={{ color: colors.text.tertiary }}
                          className="text-xs"
                        >
                          {card.description}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </Animated.View>
          )}

          {/* Post-onboarding interview prompt (no data room + no prior interview) */}
          {showInterviewPrompt && (
            <View className="w-full mt-4">
              <InterviewOnboardingCard
                colors={colors}
                onPress={handleStartInterview}
                onDismiss={() => setInterviewPromptDismissed(true)}
                title="Let's have a quick chat"
                description="This helps us understand your startup and give you an accurate readiness score — about 10 minutes, no documents needed."
                ctaLabel="Start now"
                delay={700}
              />
            </View>
          )}

          {/* Investor What's Next */}
          {!isStartup && (
            <Animated.View
              entering={entrance(FadeInUp.delay(600).duration(600))}
              className="w-full"
            >
              <View
                style={{
                  backgroundColor: colors.surface.primary,
                  borderRadius: 20,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <View className="flex-row items-center gap-2 mb-4">
                  <Sparkles size={18} color={colors.primary[600]} />
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold"
                  >
                    What's Next
                  </Text>
                </View>

                <View className="gap-3">
                  {(portfolioCount > 0
                    ? [
                        'Browse curated dealflow',
                        'Track your portfolio performance',
                        'Connect with founders',
                      ]
                    : [
                        'Browse curated dealflow',
                        'Add your portfolio companies',
                        'Build your investor profile',
                      ]
                  ).map((step, index) => (
                    <View key={`next-step-${step}`} className="flex-row items-center gap-3">
                      <View
                        className="w-6 h-6 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Text
                          style={{ color: colors.primary[600] }}
                          className="text-xs font-bold"
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <Text
                        style={{ color: colors.text.secondary }}
                        className="text-sm flex-1"
                      >
                        {step}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Portfolio Summary */}
              {portfolioCount > 0 && (
                <View
                  className="p-4 rounded-xl mt-4"
                  style={{
                    backgroundColor: colors.primary[50],
                    borderWidth: 1,
                    borderColor: colors.primary[200],
                  }}
                >
                  <View className="flex-row items-center gap-2 mb-3">
                    <Briefcase size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="font-semibold text-sm">
                      Portfolio Summary
                    </Text>
                  </View>
                  <View className="flex-row flex-wrap gap-4">
                    <View className="flex-row items-center gap-2">
                      <Building2 size={14} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.secondary }} className="text-sm">
                        {portfolioCount} {portfolioCount === 1 ? 'company' : 'companies'}
                      </Text>
                    </View>
                    {boardSeats > 0 && (
                      <View className="flex-row items-center gap-2">
                        <Crown size={14} color={colors.amber.main} />
                        <Text style={{ color: colors.text.secondary }} className="text-sm">
                          {boardSeats} board {boardSeats === 1 ? 'seat' : 'seats'}
                        </Text>
                      </View>
                    )}
                    {advisoryRoles > 0 && (
                      <View className="flex-row items-center gap-2">
                        <Star size={14} color={colors.primary[600]} />
                        <Text style={{ color: colors.text.secondary }} className="text-sm">
                          {advisoryRoles} advisory {advisoryRoles === 1 ? 'role' : 'roles'}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              )}

              {/* LinkedIn Verification (Investor) */}
              {!localInvestorProfile?.linkedInVerified && (
                <AnimatedPressable
                  onPress={() => router.push('/(onboarding)/linkedin-verify')}
                  className="flex-row items-center justify-center gap-2 py-3 rounded-xl mt-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <Linkedin size={18} color="#0A66C2" />
                  <Text style={{ color: '#0A66C2' }} className="font-medium text-sm">
                    Verify LinkedIn identity
                  </Text>
                </AnimatedPressable>
              )}

              {localInvestorProfile?.linkedInVerified && (
                <View
                  className="flex-row items-center justify-center gap-2 py-3 rounded-xl mt-4"
                  style={{
                    backgroundColor: colors.traffic.greenLight,
                    borderWidth: 1,
                    borderColor: colors.traffic.green,
                  }}
                >
                  <CheckCircle size={18} color={colors.traffic.green} />
                  <Text style={{ color: colors.traffic.green }} className="font-medium text-sm">
                    LinkedIn Verified
                  </Text>
                </View>
              )}
            </Animated.View>
          )}
        </View>

        {/* Go to Dashboard / Add Your Data Button */}
        <Animated.View
          entering={entrance(FadeInUp.delay(800).duration(500))}
          className="pb-8"
        >
          {dataSignalsLoading ? (
            <View className="items-center py-6">
              <ActivityIndicator size="small" color={colors.primary[600]} />
            </View>
          ) : !onboardingComplete ? (
            <>
              <View
                className="flex-row items-start gap-3 p-4 rounded-2xl mb-4"
                style={{
                  backgroundColor: colors.amber.light,
                  borderWidth: 1,
                  borderColor: colors.amber.main,
                }}
              >
                <AlertCircle size={20} color={colors.amber.main} />
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold mb-1"
                  >
                    One step left
                  </Text>
                  <Text
                    numberOfLines={3}
                    style={{ color: colors.text.secondary }}
                    className="text-xs"
                  >
                    Add a pitch deck, connect your data room, or complete a quick interview so we can generate your readiness score.
                  </Text>
                </View>
              </View>

              <AnimatedPressable onPress={handleAddData}>
                <LinearGradient
                  colors={[colors.primary[600], colors.primary[700]]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    borderRadius: 16,
                    paddingVertical: 18,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <Text
                    style={{ color: colors.text.inverse }}
                    className="text-base font-semibold"
                  >
                    Add Your Data
                  </Text>
                  <ArrowRight size={20} color={colors.text.inverse} />
                </LinearGradient>
              </AnimatedPressable>
            </>
          ) : (
            <AnimatedPressable
              onPress={handleGoToDashboard}
              disabled={isSaving}
            >
              <LinearGradient
                colors={isSaving ? [colors.border.medium, colors.border.medium] : [colors.primary[600], colors.primary[700]]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  borderRadius: 16,
                  paddingVertical: 18,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {isSaving ? (
                  <>
                    <ActivityIndicator size="small" color={colors.text.inverse} />
                    <Text
                      style={{ color: colors.text.inverse }}
                      className="text-base font-semibold"
                    >
                      Loading...
                    </Text>
                  </>
                ) : (
                  <>
                    <Text
                      style={{ color: colors.text.inverse }}
                      className="text-base font-semibold"
                    >
                      Go to Dashboard
                    </Text>
                    <ArrowRight size={20} color={colors.text.inverse} />
                  </>
                )}
              </LinearGradient>
            </AnimatedPressable>
          )}
        </Animated.View>
      </View>

      {scoringConsentGate.sheet}
    </OnboardingLayout>
  );
}
